import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import logoUrl from "../../../assets/logo-310x79px.png";

const GREEN = "#4cbb17";
const LIGHT_GREY = "#f5f5f5";
const BORDER_COLOUR = "#d0d0d0";

const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 9, padding: 20, backgroundColor: "#ffffff" },

  // Header bar
  header: { flexDirection: "row", alignItems: "center", marginBottom: 12 },
  logo: { height: 40, marginRight: 12, objectFit: "contain" },
  headerTextGroup: { flexDirection: "column", justifyContent: "center" },
  headerText: { color: "#333333", fontSize: 13, fontFamily: "Helvetica-Bold" },
  headerSub: { color: "#555555", fontSize: 9, marginTop: 2 },

  // Section title
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    marginBottom: 6,
    marginTop: 12,
    color: "#333",
    borderBottom: `1 solid ${BORDER_COLOUR}`,
    paddingBottom: 3,
  },

  // Two-column grid used for both info sections
  twoCol: { flexDirection: "row", gap: 30 },
  col: { flex: 1 },
  row: { flexDirection: "row", marginBottom: 4 },
  label: { fontFamily: "Helvetica-Bold", width: 110, color: "#555" },
  value: { flex: 1, color: "#111" },

  // Table
  table: { borderRadius: 2, overflow: "hidden", border: `1 solid ${BORDER_COLOUR}` },
  tableHeader: { flexDirection: "row", backgroundColor: GREEN },
  tableHeaderCell: {
    flex: 1,
    padding: "5 6",
    color: "#ffffff",
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
  },
  tableRow: { flexDirection: "row", borderTop: `1 solid ${BORDER_COLOUR}` },
  tableRowAlt: { backgroundColor: LIGHT_GREY },
  tableCell: { flex: 1, padding: "4 6", color: "#111" },
});

// ── Formatters ─────────────────────────────────────────────────────────────────

const fmtCurrency = (val: number, decimals = 2) =>
  val == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(val);

const fmtNumber = (val: number, decimals = 2) =>
  val == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(val);

const fmtDate = (val: string) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  // From stepper header
  quoteId: number;
  quoteName: string;
  clientName: string;
  contract: string;
  volume: number;
  instrumentType: string;
  knockOutType: string;
  doubleUpType: string;
  fixingFrequency: string;
  // Quote details
  quote: any;
  // Grid
  gridResults: any[];
  underlying: number;
  // Meta
  commodity: { name: string; abbreviation: string; units: string };
  isRiskTaker: boolean;
}

// ── Document ───────────────────────────────────────────────────────────────────

export const AccumulatorQuotePdf = ({
  quoteId,
  quoteName,
  clientName,
  contract,
  volume,
  instrumentType,
  knockOutType,
  doubleUpType,
  fixingFrequency,
  quote,
  gridResults,
  underlying,
  commodity,
  isRiskTaker,
}: Props) => {
  const { units } = commodity;
  const unitSingular = units.slice(0, -1);

  const margin =
    isRiskTaker ? (quote.brokerPrice - quote.fairValuePrice) / quote.maxVolume : null;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>

        {/* ── Header ── */}
        <View style={styles.header}>
          <Image src={logoUrl} style={styles.logo} />
          <View style={styles.headerTextGroup}>
            <Text style={styles.headerText}>{quoteName}</Text>
            <Text style={styles.headerSub}>
              {"Generated "}
              {new Date().toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZoneName: "short",
              })}
            </Text>
          </View>
        </View>

        {/* ── Quote information (mirrors stepper header) ── */}
        <Text style={styles.sectionTitle}>Quote Information</Text>
        <View style={styles.twoCol}>
          <View style={styles.col}>
            <InfoRow label="ID" value={String(quoteId)} />
            {clientName ? <InfoRow label="Client" value={clientName} /> : null}
            <InfoRow label="Commodity" value={`${commodity.name} (${commodity.abbreviation})`} />
            <InfoRow label="Contract" value={contract} />
            <InfoRow label="Volume" value={`${fmtNumber(volume, 0)} ${units}`} />
          </View>
          <View style={styles.col}>
            <InfoRow label="Instrument" value="Accumulator" />
            <InfoRow label="Type" value={instrumentType} />
            <InfoRow label="Knock-Out" value={knockOutType} />
            <InfoRow label="Double Up" value={doubleUpType} />
            <InfoRow label="Fixing Frequency" value={fixingFrequency} />
          </View>
        </View>

        {/* ── Quote details ── */}
        <Text style={styles.sectionTitle}>Quote Details</Text>
        <View style={styles.twoCol}>
          <View style={styles.col}>
            <InfoRow
              label="First Fixing Date"
              value={fmtDate(quote.startDate)}
            />
            <InfoRow
              label="Last Fixing Date"
              value={fmtDate(quote.expiryDate)}
            />
            <InfoRow
              label="Settlement Date"
              value={fmtDate(quote.endDate)}
            />
          </View>
          <View style={styles.col}>
            <InfoRow
              label="Maximum Volume"
              value={`${fmtNumber(quote.maxVolume, 0)} ${units}`}
            />
            <InfoRow
              label="Number of Fixings"
              value={fmtNumber(quote.numFixings, 0)}
            />
            <InfoRow
              label="Volume per Fixing"
              value={`${fmtNumber(quote.volumePerFixing, 2)} ${units}`}
            />
            <InfoRow
              label="Underlying Price"
              value={fmtCurrency(underlying || quote.underlyingPrice, 3)}
            />
            {isRiskTaker && margin != null && (
              <InfoRow label="Margin" value={`${fmtCurrency(margin, 3)} / ${unitSingular}`} />
            )}
          </View>
        </View>

        {/* ── Pricing grid ── */}
        {gridResults.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Pricing Options</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableHeaderCell}>Pricing ID</Text>
                <Text style={styles.tableHeaderCell}>Unit Price</Text>
                <Text style={styles.tableHeaderCell}>Knock-Out Level</Text>
                <Text style={styles.tableHeaderCell}>Strike</Text>
                <Text style={styles.tableHeaderCell}>Delta</Text>
              </View>
              {gridResults.map((row, i) => (
                <View
                  key={row.pricingId}
                  style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}
                >
                  <Text style={styles.tableCell}>{row.pricingId}</Text>
                  <Text style={styles.tableCell}>{fmtCurrency(row.unitPrice, 3)}</Text>
                  <Text style={styles.tableCell}>{fmtNumber(row.knockOutLevel, 4)}</Text>
                  <Text style={styles.tableCell}>{fmtNumber(row.strike, 4)}</Text>
                  <Text style={styles.tableCell}>{fmtNumber(row.delta, 2)}</Text>
                </View>
              ))}
            </View>
          </>
        )}
      </Page>
    </Document>
  );
};
